/**
 * TenantRankingsTable Component
 * "Top Tenants by Usage" — styled like the Platform Insights screenshot.
 */

import { useMemo, useState } from 'react';
import { Card, Table, Tooltip, Typography, Empty, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { useLanguage } from '@/hooks/useLanguage';
import type { TenantRanking } from '../types/insights';

const { Text } = Typography;

interface TenantRankingsTableProps {
  tenantRankings: TenantRanking[];
  loading: boolean;
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return n.toLocaleString('en-US');
}

export function TenantRankingsTable({ tenantRankings, loading }: TenantRankingsTableProps) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const [sortField, setSortField] = useState<string>('total_conversations');
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend');

  const ranked = useMemo(() => {
    const sorted = [...tenantRankings].sort((a, b) => {
      const aVal = (a as any)[sortField] as number ?? 0;
      const bVal = (b as any)[sortField] as number ?? 0;
      return sortOrder === 'descend' ? bVal - aVal : aVal - bVal;
    });
    return sorted.map((r, i) => ({ ...r, rank: i + 1 }));
  }, [tenantRankings, sortField, sortOrder]);

  const handleChange = (_: any, __: any, sorter: any) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (s?.order) {
      setSortField(s.field as string);
      setSortOrder(s.order as 'ascend' | 'descend');
    }
  };

  const columns: ColumnsType<typeof ranked[0]> = [
    {
      title: t('insights.tenantRankings.rank'),
      dataIndex: 'rank',
      key: 'rank',
      width: 52,
      render: (rank: number) => (
        <Text style={{ color: token.colorTextSecondary, fontWeight: 500, fontSize: 12 }}>{rank}</Text>
      ),
    },
    {
      title: t('insights.tenantRankings.tenantName'),
      dataIndex: 'tenant_name',
      key: 'tenant_name',
      render: (name: string) => (
        <Text style={{ fontWeight: 500, color: '#3b82f6', fontSize: 12 }}>{name}</Text>
      ),
    },
    {
      title: t('insights.agentStats.activeUsers'),
      dataIndex: 'active_users',
      key: 'active_users',
      sorter: true,
      sortOrder: sortField === 'active_users' ? sortOrder : undefined,
      render: (v: number) => <span style={{ fontSize: 12 }}>{v.toLocaleString('en-US')}</span>,
    },
    {
      title: t('insights.agentStats.conversations'),
      dataIndex: 'total_conversations',
      key: 'total_conversations',
      sorter: true,
      defaultSortOrder: 'descend',
      sortOrder: sortField === 'total_conversations' ? sortOrder : undefined,
      render: (v: number) => <span style={{ fontSize: 12 }}>{v.toLocaleString('en-US')}</span>,
    },
    {
      title: <Tooltip title={t('insights.pulse.tipMessages')}>{t('insights.pulse.messages')}</Tooltip>,
      dataIndex: 'total_messages',
      key: 'total_messages',
      sorter: true,
      sortOrder: sortField === 'total_messages' ? sortOrder : undefined,
      render: (v: number) => <span style={{ fontSize: 12 }}>{fmt(v)}</span>,
    },
    {
      title: t('insights.agentStats.tokens'),
      key: 'tokens',
      render: (_: any, row) => <span style={{ fontSize: 12 }}>{fmt(row.input_tokens + row.output_tokens)}</span>,
    },
  ];

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorderSecondary}`,
        marginBottom: 20,
      }}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ padding: '8px 14px', borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
        <Text strong style={{ fontSize: 12, fontWeight: 700, color: token.colorTextTertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('insights.tenantRankings.title')}</Text>
      </div>

      {!loading && tenantRankings.length === 0 ? (
        <div style={{ padding: 32 }}>
          <Empty description={t('insights.tenantRankings.noData')} />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={ranked}
          rowKey="tenant_id"
          loading={loading}
          onChange={handleChange}
          pagination={ranked.length > 20 ? { pageSize: 20, size: 'small' } : false}
          size="small"
          className="insights-compact-table"
        />
      )}
    </Card>
  );
}
